/**
 * Patient Medications Hook
 *
 * Fetches and caches the patient's own medications (prescribed and self-reported).
 * Uses GET /{tenantId}/patients/{patientId}/medications which returns
 * patient medication records with nested medication catalog details.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import type { ApiMedication } from "../calendar.types";
import { patientMedicationKeys } from "../patient-cache-keys";
import { throwApiError } from "../../api/api-error";

export { patientMedicationKeys };

// ============================================================================
// Types
// ============================================================================

export type PatientMedication = ApiMedication;

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch the current patient's medications.
 * Returns medication records with nested medication catalog details.
 *
 * Results are cached for 5 minutes since patient medications can change
 * during an active treatment cycle.
 */
export function usePatientMedications(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();

  return useQuery({
    queryKey: patientMedicationKeys.list(tenantId ?? "", patientId ?? ""),
    queryFn: async () => {
      if (!tenantId || !patientId) return [];

      try {
        const response = await client.GET(
          "/{tenantId}/patients/{patientId}/medications",
          {
            params: {
              path: { tenantId, patientId },
            },
          },
        );

        if (response.error) {
          throwApiError(
            response.error,
            "Failed to load patient medications",
            "patient-medications",
          );
        }

        // Handle both direct array and wrapped { data: [...] } responses
        const data = response.data;
        if (Array.isArray(data)) {
          return data as PatientMedication[];
        }
        if (data && typeof data === "object") {
          const wrapped = data as Record<string, unknown>;
          if (Array.isArray(wrapped.data)) {
            return wrapped.data as PatientMedication[];
          }
        }
        throwApiError(
          { message: "The patient medication response was not an array" },
          "Failed to load patient medications",
          "patient-medications",
        );
      } catch (error) {
        throwApiError(
          error,
          "Failed to load patient medications",
          "patient-medications",
        );
      }
    },
    enabled: enabled && !!tenantId && !!patientId,
    staleTime: 1000 * 60 * 5, // 5 minutes - patient meds can change during a cycle
    gcTime: 1000 * 60 * 30, // 30 minutes cache
  });
}
